import React from 'react';
import { Form } from '@dashboard/components/Formik';
import Link from '@dashboard/components/Link';
import usePage from '@dashboard/hooks/usePage';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import Callout from '@dashboard/components/Callout';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import SettingsDivider from '@dashboard/components/SettingsDivider';
import getStripeDashboardUrl from '@dashboard/lib/stripe/get-stripe-dashboard-url';
import { PAGE_TYPES } from '@dashboard/enums/page-types';
import { PRODUCT_TYPES } from '@dashboard/services/getProductType';
import useProduct from '@/dashboard/scenes/PageEdit/scenes/PageProduct/useProduct';

const SettingsPageStripeTax = () => {
  const { page, updatePage } = usePage();
  const { product, updateProduct } = useProduct();
  const { seller } = useAuth();

  if (!seller.tax?.enabled) {
    return (
      <div>
        <Callout variant="info">
          Stripe Tax is not yet enabled.{' '}
          <Link to="/settings/stripe-tax" variant="withIcon">
            Enable Stripe Tax <ArrowRightIcon className="w-4 h-4" />
          </Link>
        </Callout>
      </div>
    );
  }

  return (
    <div>
      <Form
        autosave
        showState={false}
        initialValues={{
          tax: page.tax || { enabled: false },
        }}
        fields={[
          {
            name: 'tax.enabled',
            type: 'checkbox',
            label: 'Enable Stripe Tax for this page',
          },
        ]}
        onSubmit={async values => {
          try {
            const { data } = await updatePage.mutateAsync(values);

            return { data };
          } catch (error) {
            return { error };
          }
        }}
      />

      <SettingsDivider />

      {page.type === PAGE_TYPES.CHECKOUT && product.type === PRODUCT_TYPES.ONE_TIME && (
        <>
          <h2 className="mb-2">Tax settings</h2>
          <Form
            autosave
            showState={false}
            initialValues={{
              taxBehavior: product.taxBehavior || '',
              taxCode: product.taxCode || '',
            }}
            fields={[
              {
                name: 'taxBehavior',
                type: 'radio',
                label: 'Tax behavior',
                labelDescription: (
                  <>
                    If the item&apos;s price should include or exclude tax. By default, it follows
                    your{' '}
                    <Link to={getStripeDashboardUrl('/settings/tax')} target="_blank">
                      Stripe Tax settings
                    </Link>
                    .
                  </>
                ),
                options: [
                  { value: '', label: 'Use Stripe account setting' },
                  { value: 'automatic', label: 'Automatic' }, // add Stripe explanations
                  { value: 'inclusive', label: 'Inclusive' },
                  { value: 'exclusive', label: 'Exclusive' },
                ],
              },
              {
                name: 'taxCode',
                type: 'select',
                label: 'Tax code',
                labelDescription: (
                  <>
                    The tax code used when calculating tax. By default, it follows the tax code from
                    your{' '}
                    <Link to={getStripeDashboardUrl('/settings/tax')} target="_blank">
                      Stripe Tax settings
                    </Link>
                    .
                  </>
                ),
                options: [
                  { value: '', label: 'Use Stripe account setting' },
                  { value: 'txcd_99999999', label: 'General - Tangible Goods' },
                  { value: 'txcd_20030000', label: 'General - Services' },
                  { value: 'txcd_10000000', label: 'General - Electronically Supplied Services	' },
                ],
                placeholder: 'Choose a tax code',
              },
            ]}
            onSubmit={async values => {
              try {
                const { data } = await updateProduct.mutateAsync(values);

                return { data };
              } catch (error) {
                return { error };
              }
            }}
          />
        </>
      )}

      {page.type === PAGE_TYPES.CHECKOUT && product.type === PRODUCT_TYPES.SUBSCRIPTION && (
        <>
          <h2 className="mb-2">Tax settings</h2>
          <p>
            Set the tax code used when calculating tax and if the tax should be inclusive or
            exclusive in your{' '}
            <Link to={getStripeDashboardUrl('/settings/tax')}>Stripe Tax settings</Link>.
          </p>
        </>
      )}

      {page.type === PAGE_TYPES.EVENT && (
        <>
          <h2 className="mb-2">Tax settings</h2>
          <Form
            autosave
            showState={false}
            initialValues={{
              taxBehavior: page.eventDetails.taxBehavior || '',
              taxCode: page.eventDetails.taxCode || '',
            }}
            fields={[
              {
                name: 'taxBehavior',
                type: 'radio',
                label: 'Tax behavior',
                labelDescription: (
                  <>
                    If the item&apos;s price should include or exclude tax. By default, it follows
                    your{' '}
                    <Link to={getStripeDashboardUrl('/settings/tax')} target="_blank">
                      Stripe Tax settings
                    </Link>
                    .
                  </>
                ),
                options: [
                  { value: '', label: 'Use Stripe account setting' },
                  { value: 'inclusive', label: 'Inclusive' },
                  { value: 'exclusive', label: 'Exclusive' },
                  { value: 'automatic', label: 'Automatic' },
                ],
              },
              {
                name: 'taxCode',
                type: 'select',
                label: 'Tax code',
                labelDescription: (
                  <>
                    The tax code used when calculating tax. By default, it follows the tax code from
                    your{' '}
                    <Link to={getStripeDashboardUrl('/settings/tax')} target="_blank">
                      Stripe Tax settings
                    </Link>
                    .
                  </>
                ),
                options: [
                  { value: '', label: 'Use Stripe account setting' },
                  { value: 'txcd_99999999', label: 'General - Tangible Goods' },
                  { value: 'txcd_20030000', label: 'General - Services' },
                  { value: 'txcd_10000000', label: 'General - Electronically Supplied Services' },
                ],
                placeholder: 'Choose a tax code',
              },
            ]}
            onSubmit={async values => {
              try {
                const { data } = await updatePage.mutateAsync({
                  eventDetails: {
                    ...page.eventDetails,
                    ...values,
                  },
                });

                return { data };
              } catch (error) {
                return { error };
              }
            }}
          />
        </>
      )}
    </div>
  );
};

export default SettingsPageStripeTax;
